import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';


import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {selectDeprecatedAccessModulesMap} from 'src/hooks/deprecatedAccessModules/services/deprecated-access-modules-slice';
import {useSupportedLicenseTypes} from 'src/hooks/dictionaries';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {CREATE_ACCOUNT_STEPS} from 'src/modules/create-account/steps';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {
    LicenseSection,
    useLicense,
} from 'src/modules/profile/account/create-license/sections/license';
import {
    AccessToModulesSection,
    useLicenseDataSets,
    usePlatformFeatureModules,
    usePlatformFeatures,
} from 'src/modules/profile/account/tabs/license-and-features/features-and-limits-tab/sections/access-to-modules';
import {
    LicenseUsageLimitsView,
    useCreateLicenseLimits,
    useDefaultLicenseLimits,
} from 'src/modules/usage-limits';
import {useAppSelector} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';

import {CreateAccountStep} from '../../create-account-step';

import type {MessageContextType} from 'src/contexts/message-context';

type Props = {
    accountId: number,
    accountEntityType: string,
    onBack: () => void,
    onRedirectToStep: (step: CREATE_ACCOUNT_STEPS) => void,
};

export const LicenseCreateStep = (props: Props) => {
    const {onBack, accountId, onRedirectToStep, accountEntityType} = props;
    const navigate = useNavigate();

    const {accountCreateSubmit, accountFieldChange} = useEventTracking();
    const {canEditAccountLimits} = useAdminAccessRight();
    const {canCreateLicense} = useAdminAccessRight();

    const onTrackSubmit = useCallback((action: string) => {
        accountCreateSubmit({
            place: 'License Create',
            action,
        });
    }, [accountCreateSubmit]);

    const [busy, setBusy] = useState(false);
    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {isCurrentChunkChanged, onShowModal, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'create-license',
        onProceed: () => {
            onTrackSubmit('Finish');
            navigate(`/profile/account/${accountId}/`);
        },
    });

    const {
        licenseLoading,
        license,
        setLicense,
        createLicense,
        isInitialLicense,
    } = useLicense({accountId, accountEntityType});

    const {
        isLoading: isDefaultLicenseLimitsLoading,
        loadingError: defaultLicenseLimitsError,
        data: defaultLicenseLimits,
        loadData: loadDefaultLicenseLimits,
    } = useDefaultLicenseLimits({licenseType: license.licenseType});

    const {
        isLoading: isUsageLimitsLoading,
        loadingError: usageLimitsLoadingError,
        data: usageLimits,
        setData: setUsageLimits,
        isChanged: isChangedUsageLimits,
        saveChanges: saveUsageLimits,
        loadData: loadUsageLimits,
    } = useCreateLicenseLimits({defaultLicenseLimits});

    const {
        loading: dataSetsLoading,
        dataSets,
        setDataSets,
        dataSetsTree,
        isInitialDataSets,
        isDefaultDataSets,
        onSaveDataSets,
        onResetDataSets,
    } = useLicenseDataSets({licenseType: license.licenseType});

    const {
        loading: platformFeatureModulesLoading,
        platformFeatureModulesTree,
        platformFeatureModules,
        setPlatformFeatureModules,
        isInitialPlatformFeatureModules,
        onSavePlatformFeatureModules,
        isDefaultPlatformFeatureModules,
        onResetPlatformFeatureModules,
    } = usePlatformFeatureModules({accountId, licenseType: license.licenseType});

    const {
        loading: platformFeaturesLoading,
        pluginsAppsTree,
        exportOptionsTree,
        platformFeatures,
        setPlatformFeatures,
        isInitialPlatformFeatures,
        onSavePlatformFeatures,
        isDefaultPlatformFeatures,
        onResetPlatformFeatures,
    } = usePlatformFeatures({accountId, licenseType: license.licenseType});

    const {
        isLoading: supportedLicenseTypesLoading,
        loadingError: supportedLicenseTypesError,
        data: supportedLicenseTypes,
    } = useSupportedLicenseTypes({accountId});

    const deprecatedAccessModulesMap = useAppSelector(selectDeprecatedAccessModulesMap);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadDefaultLicenseLimits();
        }
    }, license.licenseType);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadUsageLimits();
        }
    }, defaultLicenseLimits);

    useEffect(() => {
        const changed = !isInitialLicense || isChangedUsageLimits || !isInitialDataSets ||
            !isInitialPlatformFeatureModules || !isInitialPlatformFeatures;

        setIsChunkChanged(changed);
    }, [
        setIsChunkChanged,
        isInitialLicense, isChangedUsageLimits, isInitialDataSets,
        isInitialPlatformFeatureModules, isInitialPlatformFeatures,
    ]);

    const onOpenAccount = useCallback(() => {
        onTrackSubmit('Close');
        navigate(`/profile/account/${accountId}/`);
    }, [navigate, onTrackSubmit, accountId]);

    const onMoveBack = useCallback(() => {
        onBack();
        onTrackSubmit('Back');
    }, [onBack, onTrackSubmit]);

    const onBackClick = useCallback(() => {
        if (isCurrentChunkChanged) {
            onShowModal(onMoveBack);
        } else {
            onMoveBack();
        }
    }, [isCurrentChunkChanged, onMoveBack, onShowModal]);

    const redirectToStep = useCallback((step: CREATE_ACCOUNT_STEPS) => {
        if (isCurrentChunkChanged) {
            onShowModal(() => onRedirectToStep(step));
        } else {
            onRedirectToStep(step);
        }
    }, [isCurrentChunkChanged, onRedirectToStep, onShowModal]);

    const onFinish = useCallback(() => {
        setBusy(true);
        createLicense(async (licenseId: number) => {
            const callResults = [];

            if (isChangedUsageLimits) {
                callResults.push(await saveUsageLimits(licenseId));
            }

            if (!dataSets.length) {
                if (!isInitialPlatformFeatureModules) {
                    callResults.push(await onSavePlatformFeatureModules(licenseId));
                }

                if (!isInitialDataSets) {
                    callResults.push(await onSaveDataSets(licenseId));
                }
            } else {
                if (!isInitialDataSets) {
                    callResults.push(await onSaveDataSets(licenseId));
                }

                if (!isInitialPlatformFeatureModules) {
                    callResults.push(await onSavePlatformFeatureModules(licenseId));
                }
            }

            if (!isInitialPlatformFeatures) {
                callResults.push(await onSavePlatformFeatures(licenseId));
            }

            const errors = [...new Set(callResults.filter(Boolean))];
            setBusy(false);

            if (errors.length) {
                setErrorMessage(errors.join(' '));
            } else {
                proceed();
            }
        }, error => {
            setBusy(false);
            setErrorMessage(error);
        }, !dataSets.length && !platformFeatureModules.length);
    }, [
        createLicense,
        dataSets,
        platformFeatureModules,
        isChangedUsageLimits,
        isInitialDataSets,
        isInitialPlatformFeatureModules,
        isInitialPlatformFeatures,
        saveUsageLimits,
        onSaveDataSets,
        onSavePlatformFeatureModules,
        onSavePlatformFeatures,
        proceed,
        setErrorMessage,
    ]);

    const onResetModules = useCallback(() => {
        onResetDataSets();
        onResetPlatformFeatureModules();
        onResetPlatformFeatures();
    }, [onResetDataSets, onResetPlatformFeatureModules, onResetPlatformFeatures]);

    const isDefaultModules = useMemo(() => (
        isDefaultDataSets && isDefaultPlatformFeatureModules && isDefaultPlatformFeatures
    ), [isDefaultDataSets, isDefaultPlatformFeatureModules, isDefaultPlatformFeatures]);

    const onFieldChange = (section: string) => (parameterName: string) => {
        accountFieldChange({
            section,
            parameterName,
            viewType: 'New',
        });
    };

    if (!canCreateLicense(accountId)) {
        return (
            <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to Create License page"/>
        );
    }

    const canEditLimits = canEditAccountLimits(accountId);

    return (
        <CreateAccountStep {...{
            selectedStep: CREATE_ACCOUNT_STEPS.LICENSE_CREATE,
            onChangeStep: redirectToStep,
            content: [
                <LicenseSection
                    key="License"
                    licenseLoading={licenseLoading || supportedLicenseTypesLoading}
                    loadingError={supportedLicenseTypesError}
                    supportedLicenseTypes={supportedLicenseTypes}
                    license={license}
                    onChangeLicense={setLicense}
                    onFieldChange={onFieldChange('License')}
                />,
                <LicenseUsageLimitsView
                    key="UsageLimits"
                    licenseType={license.licenseType}
                    isDefaultUsageLimits={isDeepEqual({prev: defaultLicenseLimits, current: usageLimits})}
                    disabled={!canEditLimits}
                    loadingError={usageLimitsLoadingError || defaultLicenseLimitsError}
                    loading={isUsageLimitsLoading || isDefaultLicenseLimitsLoading}
                    usageLimits={usageLimits}
                    defaultUsageLimits={defaultLicenseLimits}
                    onChange={setUsageLimits}
                    onFieldChange={onFieldChange('Usage Limits')}
                />,
                <AccessToModulesSection
                    key="AccessToModules"
                    licenseType={license.licenseType}
                    isDefaultModules={isDefaultModules}
                    onResetModules={onResetModules}

                    dataSetsLoading={dataSetsLoading}
                    dataSetsTree={dataSetsTree}
                    dataSets={dataSets}
                    setDataSets={setDataSets}
                    onFieldChange={onFieldChange('Access To Modules')}

                    platformFeatureModulesLoading={platformFeatureModulesLoading}
                    platformFeatureModulesTree={platformFeatureModulesTree}
                    platformFeatureModules={
                        platformFeatureModules.filter(feature => !(deprecatedAccessModulesMap?.[feature]))
                    }
                    setPlatformFeatureModules={setPlatformFeatureModules}

                    platformFeaturesLoading={platformFeaturesLoading}
                    pluginsAppsTree={pluginsAppsTree}
                    exportOptionsTree={exportOptionsTree}
                    platformFeatures={platformFeatures}
                    setPlatformFeatures={setPlatformFeatures}
                />,
            ],
            buttonsBlock: [
                <Button key="Close" variant="Type.SECONDARY" busy={busy} onClick={onOpenAccount}>Close</Button>,
                <Button key="Back" variant="Type.SECONDARY" busy={busy} onClick={onBackClick}>Back</Button>,
                <Button key="Finish" variant="Type.PRIMARY" busy={busy} onClick={onFinish}>Finish</Button>,
            ],
        }}
        />
    );
};
